import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import BlogManager from '@/components/admin/BlogManager';
import ContactManager from '@/components/admin/ContactManager';
import ResumeManager from '@/components/admin/ResumeManager';
import SEOManager from '@/components/admin/SEOManager';
import JobsManager from '@/components/admin/JobsManager';

export default function AdminDashboard() {
  const [, setLocation] = useLocation();
  const [adminUser, setAdminUser] = useState<any>(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');
    
    if (!token || !user) {
      setLocation('/admin/login');
      return;
    }
    
    try {
      setAdminUser(JSON.parse(user));
    } catch (error) {
      console.error('Error parsing admin user:', error);
      setLocation('/admin/login');
    }
  }, [setLocation]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setLocation('/admin/login');
  };

  // Stats queries
  const { data: blogStats } = useQuery({
    queryKey: ['/api/blog-posts'],
    retry: false,
  });

  const { data: contactStats } = useQuery({
    queryKey: ['/api/admin/contacts'],
    retry: false,
  });

  const { data: resumeStats } = useQuery({
    queryKey: ['/api/admin/resumes'],
    retry: false,
  });

  if (!adminUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 shadow-sm border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-primary to-secondary rounded-xl flex items-center justify-center">
                <i className="fas fa-shield-alt text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  Admin Dashboard
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Welcome back, {adminUser.username}
                </p>
              </div>
            </div>
            <Button onClick={handleLogout} variant="outline">
              <i className="fas fa-sign-out-alt mr-2"></i>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Total Blogs</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">
                      {Array.isArray(blogStats) ? blogStats.length : 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
                    <i className="fas fa-blog text-blue-600 dark:text-blue-400"></i>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Contact Forms</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">
                      {Array.isArray(contactStats) ? contactStats.length : 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center">
                    <i className="fas fa-envelope text-green-600 dark:text-green-400"></i>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Resumes</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">
                      {Array.isArray(resumeStats) ? resumeStats.length : 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-xl flex items-center justify-center">
                    <i className="fas fa-file-alt text-purple-600 dark:text-purple-400"></i>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">SEO Pages</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">5</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-xl flex items-center justify-center">
                    <i className="fas fa-search text-orange-600 dark:text-orange-400"></i>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardContent className="p-0">
              <Tabs defaultValue="blogs" className="w-full">
                <div className="border-b border-slate-200 dark:border-slate-700 px-6">
                  <TabsList className="grid w-full grid-cols-5 bg-transparent h-auto p-0">
                    <TabsTrigger 
                      value="blogs" 
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none py-4"
                    >
                      <i className="fas fa-blog mr-2"></i>
                      Blog Posts
                    </TabsTrigger>
                    <TabsTrigger 
                      value="contacts"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none py-4"
                    >
                      <i className="fas fa-envelope mr-2"></i>
                      Contacts
                    </TabsTrigger>
                    <TabsTrigger 
                      value="resumes"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none py-4"
                    >
                      <i className="fas fa-file-alt mr-2"></i>
                      Resumes
                    </TabsTrigger>
                    <TabsTrigger 
                      value="jobs"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none py-4"
                    >
                      <i className="fas fa-briefcase mr-2"></i>
                      Jobs
                    </TabsTrigger>
                    <TabsTrigger 
                      value="seo"
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none py-4"
                    >
                      <i className="fas fa-search mr-2"></i>
                      SEO Settings
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="p-6">
                  <TabsContent value="blogs" className="mt-0">
                    <BlogManager />
                  </TabsContent>
                  <TabsContent value="contacts" className="mt-0">
                    <ContactManager />
                  </TabsContent>
                  <TabsContent value="resumes" className="mt-0">
                    <ResumeManager />
                  </TabsContent>
                  <TabsContent value="jobs" className="mt-0">
                    <JobsManager />
                  </TabsContent>
                  <TabsContent value="seo" className="mt-0">
                    <SEOManager />
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}